import React from 'react';
import SharedPageLayout from './layout/SharedPageLayout';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Briefcase, Send } from 'lucide-react';

const Careers = ({ onBack, onLogin, onRegister, navigateTo }) => {
  const values = [
    {
      title: 'Ship Fast, Learn Faster',
      description: 'We move quickly, experiment often, and learn from everything we do.'
    },
    {
      title: 'Customer Obsession',
      description: 'Every decision starts with how it impacts our customers.'
    },
    {
      title: 'Radical Transparency',
      description: 'Open communication, honest feedback, and no politics.'
    },
    {
      title: 'Own Your Impact',
      description: 'Take initiative, make decisions, and own the outcomes.'
    }
  ];

  return (
    <SharedPageLayout
      title="Careers - Join Our Team | Lexa.run"
      description="Join the team building the future of AI-powered test automation. Explore open positions at Lexa.run."
      onBack={onBack}
      onLogin={onLogin}
      onRegister={onRegister}
      navigateTo={navigateTo}
    >
      {/* Hero Section */}
      <section className="pt-16 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge variant="outline" className="mb-4 text-purple-600 border-purple-200">
            Join Our Team
          </Badge>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-6">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-blue-600">
              Build the Future of Testing
            </span>
          </h1>
          <p className="text-xl text-gray-600">
            We're on a mission to make software testing effortless with AI. Join us.
          </p>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Open Positions</h2>
          
          <Card className="p-12 text-center border-gray-200/50 bg-gray-50">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-100 mb-6">
              <Briefcase className="w-8 h-8 text-purple-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">
              No Open Positions Right Now
            </h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              We don't have any open positions at the moment, but we're always looking for exceptional talent. 
              Send us your resume and we'll reach out when something opens up.
            </p>
            <Button 
              className="rounded-full bg-purple-600 hover:bg-purple-700"
              onClick={() => window.open('mailto:careers@lexa.run?subject=General Application', '_blank')}
            >
              <Send className="w-5 h-5 mr-2" />
              Send Your Resume
            </Button>
          </Card>
        </div>
      </section>

      {/* Values */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Our Values</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {values.map((value, index) => (
              <div key={index} className="p-6 border border-gray-200 rounded-xl">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-purple-600 to-blue-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Don't See a Fit?
          </h2>
          <p className="text-xl text-purple-100 mb-8">
            We're always interested in meeting talented people. Reach out anyway.
          </p>
          <a 
            href="mailto:careers@lexa.run"
            className="inline-flex items-center px-8 py-4 rounded-full bg-white text-purple-600 hover:bg-gray-100 font-semibold transition-colors"
          >
            careers@lexa.run
          </a>
        </div>
      </section>
    </SharedPageLayout>
  );
};

export default Careers;
